import { Matrix, Section } from "anketa-core/group";
import { LikertScale, Translation } from "anketa-core/i18n";
import { LikertQuestion } from "anketa-core/likert_question";
import { getYesNoAsBoolean } from "src/helper";

export const FeaturesForm = (
  setDefaultValues: boolean,
  setFeaturesStatus: (values: { bgp?: boolean; ai?: boolean }) => void,
) => {
  const features = new Section("features", [
    new Translation("en", "Features"),
    new Translation("de", "Features"),
  ]);

  features.description = [
    new Translation("en", "Set which features the customer is allowed to see"),
    new Translation("de", "Festlegen, welche Funktionen der Kunde sehen darf"),
  ];

  const enableFeatures = new Matrix("enable", [
    new Translation("en", "Enabled Fetaures"),
    new Translation("de", "Aktiviere Features"),
  ]);
  const flows = new LikertQuestion(
    "flows",
    [
      new Translation("en", "Network Flows"),
      new Translation("de", "Netzwerk Flows"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(flows);

  const certificates = new LikertQuestion(
    "certificates",
    [
      new Translation("en", "Certificates Management"),
      new Translation("de", "Zertifikatsverwaltung"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(certificates);

  const wazuh = new LikertQuestion(
    "wazuh",
    [new Translation("en", "Wazuh/SIEM"), new Translation("de", "Wazuh/SIEM")],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(wazuh);

  const openvas = new LikertQuestion(
    "openvas",
    [
      new Translation("en", "Vulnerability scanner - OpenVAS"),
      new Translation("de", "Schwachstellenscanner - OpenVAS"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(openvas);

  const nuclei = new LikertQuestion(
    "nuclei",
    [
      new Translation("en", "Vulnerability scanner - Nuclei"),
      new Translation("de", "Schwachstellenscanner - Nuclei"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(nuclei);

  const compliance = new LikertQuestion(
    "compliance",
    [
      new Translation("en", "Compliance Dashboard"),
      new Translation("de", "Compliance-Dashboard"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(compliance);

  const guacamole = new LikertQuestion(
    "guacamole",
    [
      new Translation("en", "Remote Access - Guacamole"),
      new Translation("de", "Fernzugriff - Guacamole"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(guacamole);

  const webNotification = new LikertQuestion(
    "web_push",
    [
      new Translation("en", "Background Web Notification"),
      new Translation("de", "Hintergrund-Webbenachrichtigung"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(webNotification);

  const opsanioAr = new LikertQuestion(
    "opsanioar",
    [
      new Translation("en", "opsanio Active Response (Wazuh add on)"),
      new Translation("de", "opsanio Active Response (Wazuh add on)"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(opsanioAr);

  // The opsanio Agent on the customer's endpoints. On by default: it is part of
  // the standard delivery, and only an explicit No leaves it out.
  const opsanioAgent = new LikertQuestion(
    "opsanio_agent",
    [
      new Translation("en", "opsanio Agent"),
      new Translation("de", "opsanio Agent"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(opsanioAgent);

  const BGP = new LikertQuestion(
    "bgp",
    [
      new Translation("en", "Enable BGP"),
      new Translation("de", "BGP aktivieren"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(BGP);
  BGP.addChangeListener(() => {
    const bgpValue: boolean =
      getYesNoAsBoolean(BGP.fact.raw.toString()) ?? false;
    setFeaturesStatus({
      bgp: bgpValue,
    });
  });

  const aiMl = new LikertQuestion(
    "ai_ml",
    [new Translation("en", "AI/ML"), new Translation("de", "AI/ML")],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(aiMl);
  // The AI stack is a PAID gateway, so its spend budgets have to be answerable
  // the moment it is switched on - hence the AI section appears and disappears
  // with this answer, the same way the network section follows BGP.
  aiMl.addChangeListener(() => {
    setFeaturesStatus({
      ai: getYesNoAsBoolean(aiMl.fact.raw?.toString() ?? "") ?? false,
    });
  });

  const developer = new LikertQuestion(
    "developer",
    [
      new Translation("en", "Developer Access"),
      new Translation("de", "Entwicklerzugriff"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(developer);

  const previewAccess = new LikertQuestion(
    "preview_access",
    [
      new Translation("en", "Preview Access"),
      new Translation("de", "Vorschauzugriff"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(previewAccess);

  // Cluster lifecycle owner. Yes = Cluster API: no 200-k3-server / 210-k3s
  // Terraform projects are forked, deployed or freshness-checked for this VPC,
  // and the Route53 credential is delivered from Vault in the values blob.
  const manageViaCapi = new LikertQuestion(
    "manage_via_capi",
    [
      new Translation("en", "Manage via CAPI"),
      new Translation("de", "Verwaltung über CAPI"),
    ],
    LikertScale.yes_no,
  );
  manageViaCapi.description = [
    new Translation(
      "en",
      "Cluster API owns the cluster lifecycle: no 200-k3-server / 210-k3s Terraform projects are created, deployed or checked for this VPC.",
    ),
    new Translation(
      "de",
      "Cluster API verwaltet den Cluster-Lebenszyklus: für diese VPC werden keine Terraform-Projekte 200-k3-server / 210-k3s erstellt, deployt oder geprüft.",
    ),
  ];
  enableFeatures.addChild(manageViaCapi);

  // Publishing under the CUSTOMER's own domain instead of <vpc>.exanio.net.
  // Off by default and deliberately its own switch, not a side effect of the
  // certificate answer: it is a whole migration - every ingress host moves to
  // <service>.<tld>, Traefik's routers and its allowedHosts / CORS /
  // frame-ancestors middlewares are keyed on the new name, and a wildcard
  // certificate for *.<tld> has to exist before any of it can be cut over.
  // It gates the "Custom dashboard settings" row in the network section.
  const customDomain = new LikertQuestion(
    "custom_domain",
    [
      new Translation("en", "Own domain and certificate (customer TLD)"),
      new Translation("de", "Eigene Domain und Zertifikat (Kunden-TLD)"),
    ],
    LikertScale.yes_no,
  );
  customDomain.description = [
    new Translation(
      "en",
      "Yes publishes the dashboards under the customer's own domain: every host becomes <service>.<tld> and the customer supplies a certificate covering *.<tld>. No keeps the cluster on <vpc>.exanio.net with our own certificate.",
    ),
    new Translation(
      "de",
      "Ja veröffentlicht die Dashboards unter der eigenen Domain des Kunden: jeder Host wird zu <service>.<tld>, und der Kunde stellt ein Zertifikat für *.<tld> bereit. Nein belässt den Cluster auf <vpc>.exanio.net mit unserem eigenen Zertifikat.",
    ),
  ];
  enableFeatures.addChild(customDomain);

  // Pinned APT suite. Off by default and its own switch: it changes where the
  // appliance installs packages from, so it is rolled out per appliance. The
  // ring comes from the wave (versions.<env>.apt_ring), not from here.
  const aptDists = new LikertQuestion(
    "apt_dists",
    [
      new Translation("en", "Pinned APT suite (signed)"),
      new Translation("de", "Gepinnte APT-Suite (signiert)"),
    ],
    LikertScale.yes_no,
  );
  aptDists.description = [
    new Translation(
      "en",
      "Yes makes most install from the signed, pinned APT suite of the ring set on the appliance's wave. No leaves the appliance unpinned.",
    ),
    new Translation(
      "de",
      "Ja lässt most aus der signierten, gepinnten APT-Suite des Rings installieren, der an der Wave der Appliance gesetzt ist. Nein lässt die Appliance ungepinnt.",
    ),
  ];
  enableFeatures.addChild(aptDists);

  const opensearchWarm = new LikertQuestion(
    "opensearch_warm",
    [
      new Translation("en", "OpenSearch warm data tier"),
      new Translation("de", "OpenSearch Warm-Datenebene"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(opensearchWarm);

  const opensearchCold = new LikertQuestion(
    "opensearch_cold",
    [
      new Translation("en", "OpenSearch cold data tier"),
      new Translation("de", "OpenSearch Cold-Datenebene"),
    ],
    LikertScale.yes_no,
  );
  enableFeatures.addChild(opensearchCold);

  features.addChild(enableFeatures);

  if (setDefaultValues) {
    flows.setFactValue(0);
    certificates.setFactValue(0);
    openvas.setFactValue(1);
    nuclei.setFactValue(0);
    compliance.setFactValue(1);
    guacamole.setFactValue(1);
    webNotification.setFactValue(0);
    wazuh.setFactValue(0);
    opsanioAr.setFactValue(1);
    // yes_no: index 0 is Yes - the agent is enabled by default.
    opsanioAgent.setFactValue(0);
    BGP.setFactValue(1);
    aiMl.setFactValue(1);
    developer.setFactValue(1);
    previewAccess.setFactValue(1);
    // yes_no: 1 is No - Terraform-managed stays the default for new VPCs.
    manageViaCapi.setFactValue(1);
    // yes_no: 1 is No - the cluster stays on <vpc>.exanio.net until someone
    // asks for the customer's own domain.
    customDomain.setFactValue(1);
    // yes_no: 1 is No - appliances stay unpinned until switched on one by one.
    aptDists.setFactValue(1);
    // yes_no: 1 is No. Hot is the only tier now (opsanio/documentation#448) -
    // a No scales that tier's StatefulSet to zero replicas in the chart.
    opensearchWarm.setFactValue(1);
    opensearchCold.setFactValue(1);
  }
  return features;
};
